import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './person-picker.html';
import type { Person, PersonSuggestion } from '../services/api.types';
import { formatPersonName, listPersons, searchPersons, suggestPersonsForRect, upsertUserSetting } from '../services/api.service';
import {
  faceCropStyle,
  facePreviewContainerStyle,
  facePreviewSurfaceStyle,
  getFaceCropRect,
  type FaceRect,
} from '../services/face-crop.util';
import { getFaceZoomThresholdPx } from '../services/face-detection-settings.service';
import { getPersonPickerSplitViewEnabled, setPersonPickerSplitViewEnabled } from '../services/person-picker-settings.service';

// Two rows of 13 letters give the alphabet strip more breathing room than one cramped row of 26.
const ALPHABET_ROWS: string[][] = [
  'ABCDEFGHIJKLM'.split(''),
  'NOPQRSTUVWXYZ'.split(''),
];

// Sorts exactly the way each row is displayed (formatPersonName: "firstName lastName" plus an
// optional "nickName" tie-breaker) rather than the backend's default last-name-first ordering.
// When a person has neither first nor last name (nickname-only), formatPersonName falls back to
// showing just the nickname — sort by that in the "first name" slot too, so such entries land
// alphabetically where their displayed text actually appears instead of always floating to the top.
function sortFields(p: Person): [string, string, string] {
  if (!p.firstName && !p.lastName) return [p.nickName ?? '', '', ''];
  return [p.firstName ?? '', p.lastName ?? '', p.nickName ?? ''];
}

function comparePersonsAsDisplayed(a: Person, b: Person): number {
  const compare = (x: string, y: string) => x.localeCompare(y, undefined, { sensitivity: 'base' });
  const [aFirst, aLast, aNick] = sortFields(a);
  const [bFirst, bLast, bNick] = sortFields(b);
  return compare(aFirst, bFirst) || compare(aLast, bLast) || compare(aNick, bNick);
}

/** Modal popup for picking (or quick-creating) a person, e.g. when tagging a face marker. */
@customElement({ name: 'person-picker', template })
export class PersonPicker {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public selected!: (person: Person) => void;
  @bindable public closed!: () => void;

  // Set by callers that reuse this dialog for something other than "tag a face" (e.g. the
  // person-list's "merge persons" flow) — hides the split-view rect/suggestions panel (which
  // makes no sense without a marker rectangle) and lets the caller override the dialog title.
  @bindable public mode: 'tag' | 'merge' = 'tag';
  @bindable public dialogTitle: string | null = null;

  // Only relevant in 'tag' mode: identifies the media item and current (normalized 0..1) marker
  // rectangle being assigned a person, plus the full-size image's natural pixel dimensions and
  // URL — used to render the zoomed crop preview and fetch suggestions in the split view.
  @bindable public mediaItemId: string | null = null;
  @bindable public rect: FaceRect | null = null;
  // When the caller already computed suggestions for this exact rect (e.g. the lightbox's
  // auto-detection sidebar), pass them here so the split view reuses them verbatim instead of
  // re-fetching via `SuggestPersonsForRect` — that RPC has no notion of margin-expansion, so a
  // second fetch against a margin-expanded pending-face rect can silently return different
  // (often worse) results for what the user perceives as "the same face". `null`/unset means
  // "no precomputed suggestions" — falls back to fetching on open, as before.
  @bindable public initialSuggestions: PersonSuggestion[] | null = null;
  @bindable public imageUrl: string | null = null;
  @bindable public naturalWidth = 0;
  @bindable public naturalHeight = 0;
  @bindable public rotationDegrees = 0;

  public query = '';
  public results: Person[] = [];
  public loading = false;
  public showCreateModal = false;
  public alphabetRows = ALPHABET_ROWS;
  public activeLetter: string | null = null;

  public listEl: HTMLElement | null = null;

  public splitViewEnabled = getPersonPickerSplitViewEnabled();
  public suggestions: PersonSuggestion[] = [];
  public loadingSuggestions = false;

  private _debounce: ReturnType<typeof setTimeout> | null = null;

  public formatPersonName = formatPersonName;

  public get canShowSplitView(): boolean {
    return this.mode === 'tag' && !!this.rect && !!this.mediaItemId;
  }

  public get defaultTitle(): string {
    return this.i18n.tr('persons.picker.title');
  }

  public get showSplitView(): boolean {
    return this.canShowSplitView && this.splitViewEnabled;
  }

  public async attached(): Promise<void> {
    // Always start fresh: cleared filter text and the default (usage-based) sort order,
    // regardless of how the dialog was left last time it was open.
    this.query = '';
    this.activeLetter = null;
    await this._loadTop();
    if (this.showSplitView) {
      if (this.initialSuggestions) {
        this.suggestions = this.initialSuggestions;
      } else {
        await this._loadSuggestions();
      }
    }
  }

  public toggleSplitView(): void {
    this.splitViewEnabled = !this.splitViewEnabled;
    setPersonPickerSplitViewEnabled(this.splitViewEnabled);
    void upsertUserSetting('personPickerSplitViewEnabled', String(this.splitViewEnabled)).catch(() => {});
    if (!this.showSplitView || this.suggestions.length > 0) return;
    if (this.initialSuggestions) {
      this.suggestions = this.initialSuggestions;
    } else {
      void this._loadSuggestions();
    }
  }

  private async _loadSuggestions(): Promise<void> {
    if (!this.mediaItemId || !this.rect) return;
    this.loadingSuggestions = true;
    try {
      this.suggestions = await suggestPersonsForRect(this.mediaItemId, this.rect);
    } catch {
      this.suggestions = [];
    } finally {
      this.loadingSuggestions = false;
    }
  }

  public pickSuggestion(suggestion: PersonSuggestion): void {
    // The endpoint only returns the identifying fields, not a full Person — that's all callers
    // of `selected` actually need (they only read `.id`, or at most format the name for a confirm
    // dialog), so build a minimal stand-in rather than doing a redundant round-trip to fetch it.
    this.selected({
      id: suggestion.personId,
      firstName: suggestion.personFirstName,
      lastName: suggestion.personLastName,
      nickName: suggestion.personNickName,
    });
  }

  public suggestionLabel(suggestion: PersonSuggestion): string {
    return `${formatPersonName({
      firstName: suggestion.personFirstName,
      lastName: suggestion.personLastName,
      nickName: suggestion.personNickName,
    })} · ${Math.round(suggestion.score * 100)}%`;
  }

  public previewContainerStyle(): string {
    if (!this.rect) return '';
    const cropRect = getFaceCropRect(this.rect, this.naturalWidth, this.naturalHeight, getFaceZoomThresholdPx());
    return facePreviewContainerStyle(cropRect, this.naturalWidth, this.naturalHeight, 220, this.rotationDegrees);
  }

  public previewSurfaceStyle(): string {
    if (!this.rect) return '';
    const cropRect = getFaceCropRect(this.rect, this.naturalWidth, this.naturalHeight, getFaceZoomThresholdPx());
    return facePreviewSurfaceStyle(cropRect, this.naturalWidth, this.naturalHeight, 220, this.rotationDegrees);
  }

  public previewImageStyle(): string {
    if (!this.rect) return '';
    const cropRect = getFaceCropRect(this.rect, this.naturalWidth, this.naturalHeight, getFaceZoomThresholdPx());
    return faceCropStyle(cropRect);
  }

  public onInput(): void {
    this.activeLetter = null;
    if (this._debounce) clearTimeout(this._debounce);
    this._debounce = setTimeout(() => { void this._search(); }, 200);
  }

  /** Switches to alphabetical order (matching the on-screen "firstName lastName" text) and jumps
   *  the list to the first person at/after this letter. */
  public async jumpToLetter(letter: string): Promise<void> {
    if (this._debounce) clearTimeout(this._debounce);
    this.query = '';
    this.activeLetter = letter;
    this.loading = true;
    try {
      const persons = await listPersons('name', false);
      this.results = [...persons].sort(comparePersonsAsDisplayed);
    } catch {
      this.results = [];
    } finally {
      this.loading = false;
    }

    // Wait a frame so Aurelia has rendered the newly sorted list before we measure/scroll it.
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    this._scrollToLetter(letter);
  }

  private _scrollToLetter(letter: string): void {
    if (!this.listEl || this.results.length === 0) return;
    const upper = letter.toUpperCase();
    let targetIndex = this.results.findIndex(person => {
      const key = (person.firstName || person.lastName || person.nickName || '').trim().toUpperCase();
      return key >= upper;
    });
    if (targetIndex === -1) targetIndex = this.results.length - 1;

    // Align the target to the top of the list where possible; if there isn't enough content
    // left below it, the browser naturally clamps to max scroll, leaving it further down.
    const items = this.listEl.querySelectorAll<HTMLElement>('.person-picker__item');
    items[targetIndex]?.scrollIntoView({ block: 'start' });
  }

  public close(): void {
    this.closed();
  }

  public pick(person: Person): void {
    this.selected(person);
  }

  public openCreate(): void {
    this.showCreateModal = true;
  }

  public onCreateModalSaved(person: Person): void {
    this.showCreateModal = false;
    this.selected(person);
  }

  public onCreateModalClosed(): void {
    this.showCreateModal = false;
  }

  private async _loadTop(): Promise<void> {
    this.loading = true;
    try {
      this.results = await listPersons('usageCount', true);
    } catch {
      this.results = [];
    } finally {
      this.loading = false;
    }
  }

  private async _search(): Promise<void> {
    const q = this.query.trim();
    if (!q) {
      await this._loadTop();
      return;
    }
    this.loading = true;
    try {
      const { persons } = await searchPersons(q, 20, 0);
      this.results = persons;
    } catch {
      this.results = [];
    } finally {
      this.loading = false;
    }
  }
}
